import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../services/api";

const CATEGORY_META = {
  sugerencias: {
    title: "Sugerencias",
    blurb: "Cuéntanos qué producto faltaba y cómo podemos mejorar."
  },
  felicitaciones: {
    title: "Felicitaciones",
    blurb: "¡Gracias! Dejanos un mensaje para el equipo."
  },
  reclamos: {
    title: "Reclamos",
    blurb: "Por favor describe el problema (si no es devolución de producto)."
  },
  devoluciones: {
    title: "Devoluciones de producto",
    blurb:
      "Si cobró y no entregó, registramos el caso. Un operador revisará los logs. Quedate cerca de la máquina."
  }
};

function machinePublicLabel(machine) {
  if (!machine || machine.id == null) {
    return machine?.name || "";
  }
  return `Máquina ${machine.id}`;
}

export default function SupportCustomer() {
  const { machineId } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [missingProduct, setMissingProduct] = useState("");
  const [improvement, setImprovement] = useState("");
  const [message, setMessage] = useState("");
  const [chargedCard, setChargedCard] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [cameraConsent, setCameraConsent] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [existingTicketId, setExistingTicketId] = useState("");
  const [existingConsentMsg, setExistingConsentMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `${API_BASE_URL}/support/public/machines/${encodeURIComponent(machineId)}`
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "No se pudo cargar");
        if (!cancelled) setInfo(data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Error de red");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [machineId]);

  const meta = useMemo(
    () => (category ? CATEGORY_META[category] : null),
    [category]
  );

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const body = {
        machine_id: Number(machineId),
        category,
        missing_product: missingProduct || null,
        improvement_text: improvement || null,
        message: message || null,
        charged_card:
          chargedCard === "si" ? true : chargedCard === "no" ? false : null,
        camera_live_consent: cameraConsent
      };
      const res = await fetch(`${API_BASE_URL}/support/public/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "No se pudo enviar");
      setDone(data);
    } catch (err) {
      setError(err.message || "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="support-page">
        <p className="support-muted">Cargando…</p>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="support-page">
        <h1 className="support-brand">Vikingo Systems</h1>
        <p className="support-error">{error}</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="support-page">
        <h1 className="support-brand">Vikingo Systems</h1>
        <p className="support-machine">{machinePublicLabel(done.machine)}</p>
        <div className="support-card">
          <h2>Ticket #{done.ticket.id} registrado</h2>
          <p>{done.next_steps}</p>
          {error ? <p className="support-error">{error}</p> : null}
          {done.whatsapp_url ? (
            <a
              className="support-btn support-btn--primary"
              href={done.whatsapp_url}
              target="_blank"
              rel="noreferrer"
            >
              Continuar por WhatsApp
            </a>
          ) : null}
          {done.ticket?.camera_live_consent ? (
            <p className="support-muted">
              Autorizaste videollamada en vivo. No se graba ni se guarda tu imagen. Si
              un operador llama, vas a ver un aviso en la pantalla de la máquina.
            </p>
          ) : (
            <button
              type="button"
              className="support-btn support-btn--ghost"
              disabled={consentBusy}
              onClick={async () => {
                setConsentBusy(true);
                setError("");
                try {
                  const res = await fetch(
                    `${API_BASE_URL}/support/public/tickets/${done.ticket.id}/camera-consent`,
                    {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        machine_id: Number(machineId),
                        grant: true
                      })
                    }
                  );
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(data.error || "No se pudo autorizar");
                  setDone({
                    ...done,
                    ticket: { ...done.ticket, camera_live_consent: true }
                  });
                } catch (e) {
                  setError(e.message || "No se pudo autorizar");
                } finally {
                  setConsentBusy(false);
                }
              }}
            >
              {consentBusy
                ? "Guardando…"
                : "Autorizar videollamada en vivo (opcional, no se graba)"}
            </button>
          )}
          <button
            type="button"
            className="support-btn support-btn--ghost"
            onClick={() => {
              setDone(null);
              setCategory(null);
              setMessage("");
              setMissingProduct("");
              setImprovement("");
              setChargedCard("");
              setCameraConsent(false);
            }}
          >
            Volver al menú
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page">
      <h1 className="support-brand">Vikingo Systems</h1>
      <p className="support-machine">{machinePublicLabel(info.machine)}</p>
      <p className="support-lead">¿En qué te podemos ayudar?</p>
      <p className="support-privacy">
        La cámara del celular de la máquina, si se usa, es solo transmisión en vivo para
        soporte. Vikingo Systems no graba ni almacena tu imagen ni tu voz (Ley 21.719).
      </p>

      {!category ? (
        <div className="support-menu">
          {(info.categories || []).map(c => (
            <button
              key={c.id}
              type="button"
              className="support-menu__item"
              onClick={() => setCategory(c.id)}
            >
              <strong>{c.title}</strong>
              <span>{c.description}</span>
            </button>
          ))}
          <form
            className="support-card"
            style={{ marginTop: "0.75rem" }}
            onSubmit={async e => {
              e.preventDefault();
              setConsentBusy(true);
              setError("");
              setExistingConsentMsg("");
              try {
                const tid = Number(String(existingTicketId).replace(/\D/g, ""));
                if (!tid) throw new Error("Ingresá el número de ticket");
                const res = await fetch(
                  `${API_BASE_URL}/support/public/tickets/${tid}/camera-consent`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      machine_id: Number(machineId),
                      grant: true
                    })
                  }
                );
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.error || "No se pudo autorizar");
                setExistingConsentMsg(
                  `Listo: el ticket #${tid} autorizó videollamada en vivo. No se graba.`
                );
              } catch (err) {
                setError(err.message || "No se pudo autorizar");
              } finally {
                setConsentBusy(false);
              }
            }}
          >
            <p className="support-muted">
              ¿Ya tenés un ticket abierto y el operador pide video? Autorizalo acá.
            </p>
            <label className="support-label">
              Número de ticket
              <input
                value={existingTicketId}
                onChange={e => setExistingTicketId(e.target.value)}
                placeholder="Ej. 9"
                inputMode="numeric"
              />
            </label>
            {error ? <p className="support-error">{error}</p> : null}
            {existingConsentMsg ? (
              <p className="support-muted">{existingConsentMsg}</p>
            ) : null}
            <button
              type="submit"
              className="support-btn support-btn--ghost"
              disabled={consentBusy}
            >
              {consentBusy ? "Guardando…" : "Autorizar videollamada en vivo"}
            </button>
          </form>
        </div>
      ) : (
        <form className="support-card" onSubmit={submit}>
          <button
            type="button"
            className="support-back"
            onClick={() => setCategory(null)}
          >
            ← Menú
          </button>
          <h2>{meta.title}</h2>
          <p className="support-muted">{meta.blurb}</p>

          {category === "sugerencias" ? (
            <>
              <label className="support-label">
                ¿Hay algún producto que necesite y no estaba?
                <input
                  value={missingProduct}
                  onChange={e => setMissingProduct(e.target.value)}
                  placeholder="Ej. agua con gas"
                />
              </label>
              <label className="support-label">
                ¿En qué podemos mejorar?
                <textarea
                  value={improvement}
                  onChange={e => setImprovement(e.target.value)}
                  rows={3}
                  placeholder="Tu idea…"
                />
              </label>
              <label className="support-label">
                Comentario adicional (opcional)
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={2}
                />
              </label>
            </>
          ) : null}

          {category === "felicitaciones" || category === "reclamos" ? (
            <label className="support-label">
              Tu mensaje
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={4}
                required
                placeholder="Escribí aquí…"
              />
            </label>
          ) : null}

          {category === "devoluciones" ? (
            <>
              <label className="support-label">
                ¿La tarjeta cobró?
                <select
                  value={chargedCard}
                  onChange={e => setChargedCard(e.target.value)}
                >
                  <option value="">Preferir no decir</option>
                  <option value="si">Sí</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="support-label">
                ¿Qué pasó?
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={4}
                  placeholder="Ej. pagué y no salió el producto"
                />
              </label>
              <p className="support-muted">
                El operador verificará la transacción y los logs de entrega. Si
                corresponde, podrá autorizar una entrega de emergencia. Esto no
                se hace mientras SumUp está cobrando.
              </p>
            </>
          ) : null}

          {error ? <p className="support-error">{error}</p> : null}

          <label className="support-consent">
            <input
              type="checkbox"
              checked={cameraConsent}
              onChange={e => setCameraConsent(e.target.checked)}
            />
            <span>
              Solicito a esta máquina una video conferencia con un ejecutivo y la
              autorizo expresamente para procesar mi ticket más rápido. No se grabará ni
              se guarda mi imagen ni mi voz. Puedo pedir que cuelguen. No autorizo
              reconocimiento facial. Si no marco esta casilla, igual se registra el
              ticket y se atiende por logs o WhatsApp mi ticket.
            </span>
          </label>

          <button
            type="submit"
            className="support-btn support-btn--primary"
            disabled={submitting}
          >
            {submitting ? "Enviando…" : "Enviar"}
          </button>
        </form>
      )}
    </div>
  );
}
